import React, { useState, useEffect } from 'react';
import { Nav, NavItem, NavLink, TabContent, TabPane, Table } from 'reactstrap';
import classnames from 'classnames';
import PromotionReportConfig from '../PromotionReportConfig';
import GenericChart from '../../Analytics/GenericChart';

const labels = {
  '24Hours': 'Last 24 Hours',
  '48Hours': 'Last 48 Hours',
  '7Days': 'Last 7 Days',
  '14Days': 'Last 14 Days',
  '30Days': 'Last 30 Days',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
  promotionDuration: 'All Time',
};

const PromotionAnalysis = ({
  promotionId,
  promotionDetail,
  fetchPromotionDetail,
}) => {
  const { reports } = promotionDetail || {};
  const [activeTab, setActiveTab] = useState('chart');
  const [charts, setCharts] = useState([]);

  useEffect(() => {
    if (!reports || typeof reports !== 'object') return;

    try {
      // Filter top-level entries that contain 'spends'
      const topLevelData = Object.fromEntries(
        Object.entries(reports)?.filter(
          ([, value]) => value && typeof value === 'object' && 'spends' in value
        )
      );

      // Generate all chart configs in one go
      const allCharts = [generateChartOptions('Overall', topLevelData)];

      for (const [key, value] of Object.entries(reports)) {
        if (value && typeof value === 'object' && !('spends' in value)) {
          for (const [key1, value1] of Object.entries(value)) {
            const title = `${key1[0]?.toUpperCase()}${key1?.slice(1)} (${key})`;
            allCharts.push(generateChartOptions(title, value1));
          }
        }
      }

      // Single state update
      setCharts(allCharts);
    } catch (e) {
      console.error('Error generating charts:', e);
    }
  }, [reports]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const generateChartOptions = (titlePrefix, dataset) => {
    try {
      const xAxisData = Object.keys(labels).map((key) => labels[key]);
      const spendData = Object.keys(labels).map(
        (key) => dataset[key]?.spends ?? 0
      );
      const revenueData = Object.keys(labels).map(
        (key) => dataset[key]?.revenue ?? 0
      );

      const transactionsData = Object.keys(labels).map(
        (key) => dataset[key]?.noOfTransactions ?? 0
      );

      return {
        title: `${titlePrefix} -  Spend & Revenue Over Time`,
        options: {
          tooltip: { trigger: 'axis' },
          legend: { data: ['Spend', 'Revenue', 'Transactions'] },
          xAxis: { type: 'category', data: xAxisData },
          yAxis: { type: 'value' },
          series: [
            {
              name: 'Spend',
              type: 'bar',
              smooth: true,
              areaStyle: {},
              data: spendData,
            },
            {
              name: 'Revenue',
              type: 'bar',
              smooth: true,
              areaStyle: {},
              data: revenueData,
            },
            {
              name: 'Transactions',
              type: 'bar',
              smooth: true,
              areaStyle: {},
              data: transactionsData,
            },
          ],
        },
        height: 400,
      };
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Promotion Reports</h5>
        <PromotionReportConfig
          promotionId={promotionId}
          promotionDetail={promotionDetail}
          fetchPromotionDetail={fetchPromotionDetail}
        />
      </div>

      {/* Tab Navigation */}
      <Nav tabs className="mb-3">
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === 'chart' })}
            onClick={() => toggleTab('chart')}
            style={{ cursor: 'pointer' }}
          >
            Chart View
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === 'table' })}
            onClick={() => toggleTab('table')}
            style={{ cursor: 'pointer' }}
          >
            Tabular View
          </NavLink>
        </NavItem>
      </Nav>

      {/* Tab Content */}
      <TabContent activeTab={activeTab}>
        {/* Tabular View */}
        <TabPane tabId="table">
          {reports && (
            <Table bordered hover responsive className="table-striped">
              <thead className="table-primary text-white">
                <tr>
                  <th>Label</th>
                  <th className="text-end">Spends</th>
                  <th className="text-end">Revenue</th>
                  <th className="text-center">No. of Transactions</th>
                  <th>Rewards</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(reports || {}).map(([key, value]) =>
                  value?.noOfTransactions ? (
                    <tr key={key}>
                      <td>{labels[key]}</td>
                      <td className="text-end">
                        {value?.spends ? `${value.spends.toFixed(2)} AED` : '-'}
                      </td>
                      <td className="text-end">
                        {value?.revenue
                          ? `${value.revenue.toFixed(2)} AED`
                          : '-'}
                      </td>
                      <td className="text-center">
                        {value?.noOfTransactions || '-'}
                      </td>
                      <td>
                        {Object.keys(reports?.rewards || {}).length > 0
                          ? Object.entries(reports.rewards).map(
                              ([rKey, reward]) =>
                                reward[key] ? (
                                  <div
                                    key={rKey}
                                    className="border rounded p-2 mb-2"
                                  >
                                    <strong>{rKey}:</strong>{' '}
                                    {reward[key].pointsIssued} Pts,{' '}
                                    {reward[key].noOfTransactions} Transactions
                                  </div>
                                ) : null
                            )
                          : '-'}
                      </td>
                    </tr>
                  ) : (
                    Object.entries(value || {}).map(([key1, value1]) => (
                      <React.Fragment key={key1}>
                        <tr className="table-secondary">
                          <td colSpan="6" className="text-uppercase fw-bold">
                            {`${key} - ${key1}`}
                          </td>
                        </tr>
                        {Object.entries(value1 || {}).map(
                          ([key2, subValue]) => (
                            <tr key={key2}>
                              <td>{labels[key2]}</td>
                              <td className="text-end">
                                {subValue?.spends
                                  ? `${subValue.spends.toFixed(2)} ${
                                      key1.toLowerCase() === 'ae'
                                        ? 'AED'
                                        : key1.toLowerCase() === 'om'
                                        ? 'OMR'
                                        : key1.toLowerCase() === 'bh'
                                        ? 'BHD'
                                        : key1.toLowerCase() === 'qa'
                                        ? 'QAR'
                                        : key1.toLowerCase() === 'sa'
                                        ? 'SAR'
                                        : 'AED'
                                    }`
                                  : '-'}
                              </td>
                              <td className="text-end">
                                {subValue?.revenue
                                  ? `${subValue.revenue.toFixed(2)} ${
                                      key1.toLowerCase() === 'ae'
                                        ? 'AED'
                                        : key1.toLowerCase() === 'om'
                                        ? 'OMR'
                                        : key1.toLowerCase() === 'bh'
                                        ? 'BHD'
                                        : key1.toLowerCase() === 'qa'
                                        ? 'QAR'
                                        : key1.toLowerCase() === 'sa'
                                        ? 'SAR'
                                        : 'AED'
                                    }`
                                  : '-'}
                              </td>
                              <td className="text-center">
                                {subValue?.noOfTransactions || '-'}
                              </td>
                              <td>
                                {subValue?.pointsIssued ? (
                                  <div className="border rounded p-2">
                                    <strong>Points:</strong>{' '}
                                    {subValue.pointsIssued} Pts
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </td>
                            </tr>
                          )
                        )}
                      </React.Fragment>
                    ))
                  )
                )}
              </tbody>
            </Table>
          )}
        </TabPane>

        {/* Chart View */}
        <TabPane tabId="chart">
          <div className="d-flex flex-wrap">
            {charts?.map((chart, idx) => (
              <div className="col-12 mb-4">
                <GenericChart
                  key={idx}
                  title={chart.title}
                  options={chart.options}
                  height={chart.height}
                />
              </div>
            ))}
          </div>
        </TabPane>
      </TabContent>
    </div>
  );
};

export default PromotionAnalysis;
